import AwsS3, { type AwsBody } from '@uppy/aws-s3';
import Uppy, { type UppyFile } from '@uppy/core';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useNotifications } from '../../stores/notificationStore';
import { useAuth } from '../../stores/authStore';
import { useI18n } from 'vue-i18n';

export type UppyMeta = Record<string, unknown>;

export interface UseUppyOptions {
  clearOnUnmount?: boolean;
  registerNotifications?: boolean;
}

const uppy = new Uppy<UppyMeta, AwsBody>().use(AwsS3, {
  id: 'AwsS3Multipart',
  endpoint: `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/upload`,
});

export const useUppy = ({ clearOnUnmount, registerNotifications }: UseUppyOptions = {}) => {
  const { authToken, refreshLogin } = useAuth();

  watch(
    authToken,
    (token) => {
      if (!token) return;
      uppy
        .getPlugin('AwsS3Multipart')
        ?.setOptions({ headers: { Authorization: `Bearer ${token}` } });
    },
    { immediate: true },
  );

  if (registerNotifications) {
    const { t } = useI18n();
    const { addNotification, updateNotification } = useNotifications();
    const progressNotificationId = ref<string | null>(null);

    const onUpload = () => {
      if (progressNotificationId.value) return progressNotificationId.value;

      progressNotificationId.value = `upload-progress-${Date.now()}`;

      addNotification({
        id: progressNotificationId.value,
        title: t('notification.upload.progress.title'),
        message: t('notification.upload.progress.message'),
        type: 'info',
        progress: 0,
      });

      return progressNotificationId.value;
    };

    const onProgress = (progress: number) => {
      if (!progressNotificationId.value || progress === 0) return;
      updateNotification(progressNotificationId.value, { progress });
    };

    const onComplete = () => {
      if (!progressNotificationId.value) return;

      updateNotification(progressNotificationId.value, {
        title: t('notification.upload.complete.title'),
        message: t('notification.upload.complete.message'),
        type: 'success',
        progress: null,
      });
      progressNotificationId.value = null;
    };

    const onError = ({ message }: Error) => {
      if (!progressNotificationId.value) return;

      updateNotification(progressNotificationId.value, {
        title: t('notification.upload.failed.title'),
        message: t('notification.upload.failed.message', { message }),
        type: 'error',
        progress: null,
      });
      progressNotificationId.value = null;
    };

    const onAuthError = async (
      error: { name: string },
      file?: UppyFile<UppyMeta, AwsBody>,
      response?: { status?: number },
    ) => {
      if (response?.status !== 401 || !file) return;

      try {
        await refreshLogin();
        uppy
          .getPlugin('AwsS3')
          ?.setOptions({ headers: { Authorization: `Bearer ${authToken.value}` } });
        await uppy.retryUpload(file.id);
      } catch {
        // refreshLogin() already logs the user out on failure
      }
    };

    onMounted(() => {
      uppy.on('upload', onUpload);
      uppy.on('progress', onProgress);
      uppy.on('complete', onComplete);
      uppy.on('error', onError);
      uppy.on('error', onAuthError);
    });

    onUnmounted(() => {
      uppy.off('upload', onUpload);
      uppy.off('progress', onProgress);
      uppy.off('complete', onComplete);
      uppy.off('error', onError);
      uppy.off('error', onAuthError);
    });
  }

  if (clearOnUnmount) {
    onUnmounted(() => {
      uppy.clear();
    });
  }

  return { uppy };
};
